import React, { useState } from 'react';
import { LineChart, Line, AreaChart, Area, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const AgentOrchestration = () => {
  const [selectedScenario, setSelectedScenario] = useState('codebase');
  const [viewMode, setViewMode] = useState('overview');
  const [orchestrationMode, setOrchestrationMode] = useState('collaborative');
  const [showComparison, setShowComparison] = useState(true);

  // Scenarios for different orchestration examples
  const scenarios = {
    codebase: {
      title: "Large Codebase Refactoring",
      description: "Modernizing a 500k LOC legacy system",
      agents: [
        { id: 1, name: "Architecture Agent", role: "System design", model: "GPT-4", status: "analyzing", progress: 85 },
        { id: 2, name: "Security Agent", role: "Vulnerability scan", model: "Claude-3", status: "active", progress: 60 },
        { id: 3, name: "Performance Agent", role: "Optimization", model: "Gemini", status: "waiting", progress: 0 },
        { id: 4, name: "Testing Agent", role: "Test generation", model: "Llama-2", status: "active", progress: 45 },
        { id: 5, name: "Documentation Agent", role: "API docs", model: "GPT-3.5", status: "completed", progress: 100 }
      ]
    },
    ml: {
      title: "ML Pipeline Optimization",
      description: "Building end-to-end ML system for fraud detection",
      agents: [
        { id: 1, name: "Data Agent", role: "Feature engineering", model: "GPT-4", status: "completed", progress: 100 },
        { id: 2, name: "Model Agent", role: "Architecture search", model: "Claude-3", status: "active", progress: 75 },
        { id: 3, name: "Training Agent", role: "Hyperparameter tuning", model: "Gemini", status: "active", progress: 40 },
        { id: 4, name: "Evaluation Agent", role: "Performance metrics", model: "GPT-3.5", status: "waiting", progress: 0 },
        { id: 5, name: "Deployment Agent", role: "Production setup", model: "Llama-2", status: "waiting", progress: 0 }
      ]
    },
    trading: {
      title: "Trading Strategy Development",
      description: "Multi-market arbitrage system",
      agents: [
        { id: 1, name: "Market Agent", role: "Data analysis", model: "GPT-4", status: "active", progress: 90 },
        { id: 2, name: "Strategy Agent", role: "Signal generation", model: "Claude-3", status: "active", progress: 70 },
        { id: 3, name: "Risk Agent", role: "Risk assessment", model: "Gemini", status: "active", progress: 85 },
        { id: 4, name: "Execution Agent", role: "Order management", model: "GPT-3.5", status: "waiting", progress: 0 },
        { id: 5, name: "Monitoring Agent", role: "Performance tracking", model: "Llama-2", status: "active", progress: 55 }
      ]
    }
  };

  // Orchestration patterns
  const orchestrationPatterns = {
    collaborative: {
      name: "Collaborative Swarm",
      description: "Agents work together, sharing insights",
      benefits: ["Collective intelligence", "Real-time coordination", "Emergent solutions"],
      icon: "🤝"
    },
    competitive: {
      name: "Competitive Evolution",
      description: "Agents compete for best solution",
      benefits: ["Natural selection", "Diverse approaches", "Performance driven"],
      icon: "⚔️"
    },
    hierarchical: {
      name: "Hierarchical Command",
      description: "Master agent coordinates specialists",
      benefits: ["Clear leadership", "Organized workflow", "Efficient delegation"],
      icon: "👑"
    },
    pipeline: {
      name: "Pipeline Processing",
      description: "Sequential agent handoffs",
      benefits: ["Clear stages", "Quality gates", "Predictable flow"],
      icon: "🔄"
    }
  };

  // Performance metrics comparison
  const performanceData = [
    { metric: 'Speed', single: 65, swarm: 95, traditional: 40 },
    { metric: 'Quality', single: 70, swarm: 92, traditional: 60 },
    { metric: 'Coverage', single: 50, swarm: 98, traditional: 30 },
    { metric: 'Innovation', single: 60, swarm: 88, traditional: 45 },
    { metric: 'Reliability', single: 80, swarm: 85, traditional: 75 }
  ];

  // Agent communication data
  const communicationData = [
    { time: '0s', messages: 0, insights: 0 },
    { time: '10s', messages: 15, insights: 2 },
    { time: '20s', messages: 45, insights: 8 },
    { time: '30s', messages: 80, insights: 15 },
    { time: '40s', messages: 120, insights: 25 },
    { time: '50s', messages: 150, insights: 35 },
    { time: '60s', messages: 170, insights: 42 }
  ];

  // Cost-benefit analysis
  const costBenefitData = [
    { agents: 1, cost: 10, value: 20, roi: 100 },
    { agents: 3, cost: 25, value: 80, roi: 220 },
    { agents: 5, cost: 40, value: 150, roi: 275 },
    { agents: 8, cost: 60, value: 200, roi: 233 },
    { agents: 10, cost: 75, value: 220, roi: 193 }
  ];

  const currentScenario = scenarios[selectedScenario];

  // Calculate swarm statistics
  const swarmStats = {
    totalAgents: currentScenario.agents.length,
    activeAgents: currentScenario.agents.filter(a => a.status === 'active').length,
    completedTasks: currentScenario.agents.filter(a => a.status === 'completed').length,
    avgProgress: Math.round(currentScenario.agents.reduce((sum, a) => sum + a.progress, 0) / currentScenario.agents.length)
  };

  // Agent status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return '#4ECDC4';
      case 'completed': return '#96CEB4';
      case 'waiting': return '#FFD700';
      case 'analyzing': return '#DDA0DD';
      default: return '#666';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
      color: 'white',
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 30px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        background: 'rgba(0, 0, 0, 0.3)'
      }}>
        <h1 style={{ 
          margin: 0, 
          fontSize: '28px',
          fontWeight: '700',
          background: 'linear-gradient(135deg, #4ECDC4, #45B7D1)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}>
          🌐 Agent Orchestration & Swarms
        </h1>
        <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
          Beyond Simple Agents: Coordinated AI Teams That Deliver Enterprise Results
        </p>
      </div>

      {/* Scenario Selector */}
      <div style={{
        padding: '20px 30px',
        background: 'rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <div style={{ marginBottom: '15px' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px' }}>Select Scenario</h3>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {Object.entries(scenarios).map(([key, scenario]) => (
              <button
                key={key}
                onClick={() => setSelectedScenario(key)}
                style={{
                  padding: '10px 20px',
                  background: selectedScenario === key ? 
                    'linear-gradient(135deg, #4ECDC4, #45B7D1)' : 
                    'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  borderRadius: '25px',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s ease'
                }}
              >
                {key === 'codebase' ? '💻 ' : key === 'ml' ? '🤖 ' : '📈 '}
                {scenario.title}
              </button>
            ))}
          </div>
        </div>

        {/* View Mode Tabs */}
        <div style={{ display: 'flex', gap: '10px' }}>
          {['overview', 'orchestration', 'performance', 'comparison'].map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              style={{
                padding: '8px 16px',
                background: viewMode === mode ? 
                  'rgba(255, 255, 255, 0.2)' : 
                  'transparent',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '20px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '13px',
                textTransform: 'capitalize',
                transition: 'all 0.3s ease'
              }}
            >
              {mode}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '30px' }}>
        {/* Overview View */}
        {viewMode === 'overview' && (
          <div>
            {/* Current Task */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px',
              border: '1px solid rgba(78, 205, 196, 0.3)'
            }}>
              <h2 style={{ margin: '0 0 10px 0', fontSize: '20px' }}>
                {currentScenario.title}
              </h2>
              <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                {currentScenario.description}
              </p>
            </div>

            {/* Swarm Status Dashboard */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#4ECDC4' }}>
                  {swarmStats.totalAgents}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Total Agents</div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#96CEB4' }}>
                  {swarmStats.activeAgents}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Active Now</div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#FFD700' }}>
                  {swarmStats.completedTasks}
                </div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Completed</div>
              </div>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '36px', fontWeight: '700', color: '#DDA0DD' }}>
                  {swarmStats.avgProgress}%
                </div>
                <div style={{ fontSize: '14px', opacity: 0.8 }}>Avg Progress</div>
              </div>
            </div>

            {/* Agent Grid */}
            <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>Active Agent Swarm</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              {currentScenario.agents.map(agent => (
                <div key={agent.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '12px',
                  padding: '20px',
                  border: `2px solid ${getStatusColor(agent.status)}40`,
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  {/* Progress bar background */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: `${agent.progress}%`,
                    height: '100%',
                    background: `linear-gradient(90deg, ${getStatusColor(agent.status)}20, transparent)`,
                    transition: 'width 0.5s ease'
                  }} />
                  
                  <div style={{ position: 'relative', zIndex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <h4 style={{ margin: 0, fontSize: '16px' }}>{agent.name}</h4>
                      <span style={{
                        background: `${getStatusColor(agent.status)}30`,
                        color: getStatusColor(agent.status),
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {agent.status.toUpperCase()}
                      </span>
                    </div>
                    
                    <div style={{ fontSize: '13px', opacity: 0.8, marginBottom: '5px' }}>
                      {agent.role} • {agent.model}
                    </div>
                    
                    <div style={{ marginTop: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '5px' }}>
                        <span>Progress</span>
                        <span>{agent.progress}%</span>
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        background: 'rgba(255, 255, 255, 0.1)',
                        borderRadius: '3px',
                        overflow: 'hidden'
                      }}>
                        <div style={{
                          width: `${agent.progress}%`,
                          height: '100%',
                          background: getStatusColor(agent.status),
                          borderRadius: '3px',
                          transition: 'width 0.5s ease'
                        }} />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Communication Flow */}
            <div style={{
              marginTop: '40px',
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px'
            }}>
              <h3 style={{ fontSize: '18px', marginBottom: '20px' }}>
                Inter-Agent Communication
              </h3>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={communicationData}>
                  <defs>
                    <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4ECDC4" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#4ECDC4" stopOpacity={0.1}/>
                    </linearGradient>
                    <linearGradient id="colorInsights" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#FFD700" stopOpacity={0.8}/>
                      <stop offset="95%" stopColor="#FFD700" stopOpacity={0.1}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="time" stroke="white" fontSize={12} />
                  <YAxis stroke="white" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(0,0,0,0.8)', 
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="messages" 
                    stroke="#4ECDC4" 
                    fillOpacity={1} 
                    fill="url(#colorMessages)"
                    name="Messages Exchanged"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="insights" 
                    stroke="#FFD700" 
                    fillOpacity={1} 
                    fill="url(#colorInsights)"
                    name="Insights Generated"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Orchestration Patterns View */}
        {viewMode === 'orchestration' && (
          <div>
            <h3 style={{ fontSize: '20px', marginBottom: '25px' }}>
              Orchestration Patterns
            </h3>
            
            {/* Pattern Selector */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '30px'
            }}>
              {Object.entries(orchestrationPatterns).map(([key, pattern]) => (
                <div
                  key={key}
                  onClick={() => setOrchestrationMode(key)}
                  style={{
                    background: orchestrationMode === key ? 
                      'linear-gradient(135deg, rgba(78, 205, 196, 0.2), rgba(69, 183, 209, 0.2))' :
                      'rgba(255, 255, 255, 0.05)',
                    borderRadius: '12px',
                    padding: '20px',
                    border: orchestrationMode === key ?
                      '2px solid rgba(78, 205, 196, 0.5)' :
                      '1px solid rgba(255, 255, 255, 0.1)',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div style={{ fontSize: '32px', marginBottom: '10px' }}>{pattern.icon}</div>
                  <h4 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{pattern.name}</h4>
                  <p style={{ margin: '0 0 15px 0', fontSize: '12px', opacity: 0.8 }}>
                    {pattern.description}
                  </p>
                  <div>
                    {pattern.benefits.map((benefit, idx) => (
                      <div key={idx} style={{
                        fontSize: '11px',
                        padding: '4px 0',
                        borderTop: idx === 0 ? '1px solid rgba(255,255,255,0.1)' : 'none'
                      }}>
                        ✓ {benefit}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Pattern Visualization */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '30px',
              textAlign: 'center'
            }}>
              <h4 style={{ fontSize: '18px', marginBottom: '20px' }}>
                {orchestrationPatterns[orchestrationMode].name} in Action
              </h4>
              
              {/* Simplified visualization based on pattern */}
              {orchestrationMode === 'collaborative' && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{
                      width: '80px',
                      height: '80px',
                      background: 'linear-gradient(135deg, #4ECDC4, #45B7D1)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '32px',
                      marginBottom: '10px'
                    }}>
                      🤖
                    </div>
                    <div style={{ fontSize: '12px' }}>Agent 1</div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ color: '#4ECDC4' }}>↔️</div>
                    <div style={{ color: '#4ECDC4' }}>↔️</div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #96CEB4, #4ECDC4)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        marginBottom: '10px'
                      }}>
                        🧠
                      </div>
                      <div style={{ fontSize: '12px' }}>Agent 2</div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px',
                        marginBottom: '10px'
                      }}>
                        ⚡
                      </div>
                      <div style={{ fontSize: '12px' }}>Agent 3</div>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ color: '#4ECDC4' }}>↔️</div>
                    <div style={{ color: '#4ECDC4' }}>↔️</div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '12px',
                    padding: '20px',
                    minWidth: '150px'
                  }}>
                    <div style={{ fontSize: '24px', marginBottom: '10px' }}>💡</div>
                    <div style={{ fontSize: '14px', fontWeight: '600' }}>Shared Insights</div>
                    <div style={{ fontSize: '12px', opacity: 0.8, marginTop: '5px' }}>
                      Collective Intelligence
                    </div>
                  </div>
                </div>
              )}

              {orchestrationMode === 'competitive' && (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '30px', marginBottom: '30px' }}>
                    {[1, 2, 3].map(i => (
                      <div key={i} style={{
                        width: '100px',
                        height: '100px',
                        background: `linear-gradient(135deg, ${i === 1 ? '#FF6B6B' : i === 2 ? '#4ECDC4' : '#96CEB4'}, ${i === 1 ? '#FF4444' : i === 2 ? '#45B7D1' : '#7BC96F'})`,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '36px'
                      }}>
                        {i === 1 ? '🏃' : i === 2 ? '🏃‍♀️' : '🏃‍♂️'}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: '24px', marginBottom: '20px' }}>⚔️</div>
                  <div style={{
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    borderRadius: '12px',
                    padding: '20px',
                    display: 'inline-block'
                  }}>
                    <div style={{ fontSize: '32px', marginBottom: '10px' }}>🏆</div>
                    <div style={{ fontSize: '16px', fontWeight: '600' }}>Best Solution Wins</div>
                  </div>
                </div>
              )}

              {orchestrationMode === 'hierarchical' && (
                <div>
                  <div style={{
                    width: '120px',
                    height: '120px',
                    background: 'linear-gradient(135deg, #DDA0DD, #9370DB)',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '48px',
                    margin: '0 auto 30px'
                  }}>
                    👑
                  </div>
                  <div style={{ fontSize: '16px', marginBottom: '20px' }}>Master Orchestrator</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
                    {['🤖', '🧠', '⚡', '🔮'].map((icon, i) => (
                      <div key={i}>
                        <div style={{ fontSize: '24px', marginBottom: '10px' }}>⬇️</div>
                        <div style={{
                          width: '60px',
                          height: '60px',
                          background: 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '28px'
                        }}>
                          {icon}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {orchestrationMode === 'pipeline' && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '20px' }}>
                  {['📥', '🤖', '🧠', '⚡', '📤'].map((icon, i) => (
                    <React.Fragment key={i}>
                      <div style={{
                        width: '80px',
                        height: '80px',
                        background: i === 0 || i === 4 ? 'rgba(255, 255, 255, 0.1)' : 
                                   `linear-gradient(135deg, ${i === 1 ? '#4ECDC4' : i === 2 ? '#96CEB4' : '#FFD700'}, ${i === 1 ? '#45B7D1' : i === 2 ? '#7BC96F' : '#FFA500'})`,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '32px'
                      }}>
                        {icon}
                      </div>
                      {i < 4 && <div style={{ fontSize: '24px', color: '#4ECDC4' }}>→</div>}
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Performance View */}
        {viewMode === 'performance' && (
          <div>
            <h3 style={{ fontSize: '20px', marginBottom: '25px' }}>
              Swarm Performance Metrics
            </h3>

            {/* Performance Comparison */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px'
            }}>
              <h4 style={{ fontSize: '16px', marginBottom: '20px' }}>
                Performance: Swarm vs Single Agent vs Traditional
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <RadarChart data={performanceData}>
                  <PolarGrid stroke="rgba(255,255,255,0.3)" />
                  <PolarAngleAxis dataKey="metric" stroke="white" fontSize={12} />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} stroke="white" fontSize={10} />
                  <Radar
                    name="Agent Swarm"
                    dataKey="swarm"
                    stroke="#4ECDC4"
                    fill="#4ECDC4"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Single Agent"
                    dataKey="single"
                    stroke="#FFD700"
                    fill="#FFD700"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Radar
                    name="Traditional"
                    dataKey="traditional"
                    stroke="#FF6B6B"
                    fill="#FF6B6B"
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(0,0,0,0.8)', 
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }} 
                  />
                  <Legend />
                </RadarChart>
              </ResponsiveContainer>
            </div>

            {/* Cost-Benefit Analysis */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px'
            }}>
              <h4 style={{ fontSize: '16px', marginBottom: '20px' }}>
                Cost-Benefit Analysis: Optimal Agent Count
              </h4>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={costBenefitData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis dataKey="agents" stroke="white" fontSize={12} label={{ value: 'Number of Agents', position: 'insideBottom', offset: -5 }} />
                  <YAxis stroke="white" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'rgba(0,0,0,0.8)', 
                      border: '1px solid rgba(255,255,255,0.2)',
                      borderRadius: '8px'
                    }}
                  />
                  <Line type="monotone" dataKey="cost" stroke="#FF6B6B" strokeWidth={2} name="Cost" />
                  <Line type="monotone" dataKey="value" stroke="#4ECDC4" strokeWidth={2} name="Value Delivered" />
                  <Line type="monotone" dataKey="roi" stroke="#FFD700" strokeWidth={2} strokeDasharray="5 5" name="ROI %" />
                  <Legend />
                </LineChart>
              </ResponsiveContainer>
              <div style={{
                marginTop: '15px',
                padding: '15px',
                background: 'rgba(78, 205, 196, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(78, 205, 196, 0.3)',
                fontSize: '13px'
              }}>
                💡 <strong>Insight:</strong> Optimal ROI achieved with 5-7 agents. Beyond this, coordination overhead reduces efficiency.
              </div>
            </div>

            {/* Real-time Metrics */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Task Completion Rate</h5>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#4ECDC4', marginBottom: '10px' }}>
                  3.2x
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Faster than single agent approach
                </div>
              </div>
              
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Error Reduction</h5>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#96CEB4', marginBottom: '10px' }}>
                  87%
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Fewer errors through peer review
                </div>
              </div>
              
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '12px',
                padding: '20px'
              }}>
                <h5 style={{ margin: '0 0 15px 0', fontSize: '14px' }}>Innovation Score</h5>
                <div style={{ fontSize: '32px', fontWeight: '700', color: '#FFD700', marginBottom: '10px' }}>
                  9.2/10
                </div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                  Novel solutions from agent collaboration
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Comparison View */}
        {viewMode === 'comparison' && (
          <div>
            <h3 style={{ fontSize: '20px', marginBottom: '25px' }}>
              Why Turintech Leads: Agent Orchestration Comparison
            </h3>

            {/* Competitor Comparison */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginBottom: '30px',
              overflow: 'auto'
            }}>
              <table style={{ 
                width: '100%', 
                fontSize: '13px',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr>
                    <th style={{ 
                      textAlign: 'left', 
                      padding: '12px',
                      borderBottom: '2px solid rgba(255,255,255,0.2)',
                      fontWeight: '600'
                    }}>
                      Feature
                    </th>
                    <th style={{ 
                      textAlign: 'center', 
                      padding: '12px',
                      borderBottom: '2px solid rgba(255,255,255,0.2)',
                      background: 'rgba(78, 205, 196, 0.1)',
                      color: '#4ECDC4'
                    }}>
                      Turintech
                    </th>
                    <th style={{ 
                      textAlign: 'center', 
                      padding: '12px',
                      borderBottom: '2px solid rgba(255,255,255,0.2)'
                    }}>
                      Competitor A
                    </th>
                    <th style={{ 
                      textAlign: 'center', 
                      padding: '12px',
                      borderBottom: '2px solid rgba(255,255,255,0.2)'
                    }}>
                      Competitor B
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Multi-Agent Support', turintech: '✅ Native', competitorA: '⚠️ Limited', competitorB: '❌ Single only' },
                    { feature: 'Agent Orchestration', turintech: '✅ 4 patterns', competitorA: '❌ None', competitorB: '⚠️ Basic' },
                    { feature: 'Inter-Agent Communication', turintech: '✅ Real-time', competitorA: '❌ No', competitorB: '❌ No' },
                    { feature: 'Swarm Intelligence', turintech: '✅ Advanced', competitorA: '❌ No', competitorB: '❌ No' },
                    { feature: 'Task Distribution', turintech: '✅ Intelligent', competitorA: '⚠️ Manual', competitorB: '❌ None' },
                    { feature: 'Collective Learning', turintech: '✅ Yes', competitorA: '❌ No', competitorB: '❌ No' },
                    { feature: 'Conflict Resolution', turintech: '✅ Automated', competitorA: '❌ No', competitorB: '❌ No' },
                    { feature: 'Performance Scaling', turintech: '✅ Linear+', competitorA: '⚠️ Limited', competitorB: '❌ Poor' },
                    { feature: 'Enterprise Ready', turintech: '✅ Yes', competitorA: '⚠️ Partial', competitorB: '❌ No' }
                  ].map((row, idx) => (
                    <tr key={idx}>
                      <td style={{ 
                        padding: '10px 12px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        {row.feature}
                      </td>
                      <td style={{ 
                        padding: '10px 12px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        textAlign: 'center',
                        background: 'rgba(78, 205, 196, 0.05)',
                        fontWeight: '600'
                      }}>
                        {row.turintech}
                      </td>
                      <td style={{ 
                        padding: '10px 12px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        textAlign: 'center'
                      }}>
                        {row.competitorA}
                      </td>
                      <td style={{ 
                        padding: '10px 12px',
                        borderBottom: '1px solid rgba(255,255,255,0.1)',
                        textAlign: 'center'
                      }}>
                        {row.competitorB}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Key Differentiators */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '20px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(78, 205, 196, 0.1), rgba(69, 183, 209, 0.1))',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(78, 205, 196, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#4ECDC4' }}>
                  🚀 What Makes Us Different
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.8' }}>
                  <li>True multi-agent orchestration, not just parallel execution</li>
                  <li>Agents learn from each other in real-time</li>
                  <li>Intelligent task distribution based on agent strengths</li>
                  <li>Conflict resolution through consensus mechanisms</li>
                  <li>Enterprise-grade monitoring and control</li>
                </ul>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.1), rgba(255, 68, 68, 0.1))',
                borderRadius: '12px',
                padding: '20px',
                border: '1px solid rgba(255, 107, 107, 0.3)'
              }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#FF6B6B' }}>
                  ⚠️ What Others Are Missing
                </h4>
                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', lineHeight: '1.8' }}>
                  <li>Single agent = single point of failure</li>
                  <li>No inter-agent communication = missed insights</li>
                  <li>Manual coordination = human bottleneck</li>
                  <li>No collective learning = repeated mistakes</li>
                  <li>Limited scalability = poor enterprise fit</li>
                </ul>
              </div>
            </div>

            {/* Success Story */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '16px',
              padding: '25px',
              marginTop: '30px'
            }}>
              <h4 style={{ margin: '0 0 15px 0', fontSize: '16px' }}>
                📈 Real-World Impact
              </h4>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '20px',
                marginBottom: '20px'
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#4ECDC4' }}>85%</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Faster Development</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#96CEB4' }}>92%</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Higher Code Quality</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#FFD700' }}>3.5x</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>ROI Improvement</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '36px', fontWeight: '700', color: '#DDA0DD' }}>24/7</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>Autonomous Operation</div>
                </div>
              </div>
              <p style={{ fontSize: '13px', lineHeight: '1.6', margin: 0 }}>
                <strong>Case Study:</strong> Fortune 500 financial services company reduced their ML model 
                development time from 6 months to 3 weeks using Turintech's agent swarm technology. 
                The swarm automatically handled data preprocessing, feature engineering, model selection, 
                and deployment optimization - tasks that previously required a team of 12 data scientists.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentOrchestration; 